import { useEffect, useMemo, useRef } from 'react'
import { getUserLabel } from './components/user-label'

// Roshambo runs entirely on top of regular chat comments — there's no
// dedicated game backend. Each protocol step (challenge/accept/commit/reveal)
// is posted as a normal comment whose text is a tagged JSON blob; every
// client (both players and spectators) derives match state by scanning the
// comment history the same way. Moves are hidden with a commit/reveal scheme
// (post a hash first, reveal move+nonce only once both sides are locked in)
// so the second player can't just read the first player's move in the log
// and pick the counter.
const PREFIX = '⚔️RSB⚔️'

export const MOVE_ALIASES = { r: 'rock', p: 'paper', s: 'scissors', rock: 'rock', paper: 'paper', scissors: 'scissors' }
const BEATS = { rock: 'scissors', paper: 'rock', scissors: 'paper' }
const MOVE_ICON = { rock: '🪨', paper: '📄', scissors: '✂️' }

const STORAGE_KEY = 'pinmonkey:roshambo:pending'

const randomId = () => Array.from(crypto.getRandomValues(new Uint8Array(8)))
  .map((b) => b.toString(16).padStart(2, '0'))
  .join('')

const toHex = (buffer) => Array.from(new Uint8Array(buffer))
  .map((b) => b.toString(16).padStart(2, '0'))
  .join('')

const hashMove = async (move, nonce) => {
  const data = new TextEncoder().encode(`${move}:${nonce}`)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return toHex(digest)
}

const encode = (payload) => `${PREFIX}${JSON.stringify(payload)}`

export const decode = (text) => {
  if (typeof text !== 'string' || !text.startsWith(PREFIX)) return null
  try {
    return JSON.parse(text.slice(PREFIX.length))
  } catch {
    return null
  }
}

const loadPending = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}
  } catch {
    return {}
  }
}

const savePending = (pending) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending))
  } catch {
    // sessionStorage unavailable (private mode, etc) — reveal just won't
    // survive a refresh, which is an acceptable degradation
  }
}

// There's no user directory here, only whoever has already posted in this
// chat, so a target can only be named once they've said something.
const findUserByUsername = (comments, username) => {
  const target = username.replace(/^@/, '').toLowerCase()
  const match = (comments || []).find((c) => c.User && getUserLabel(c.User)?.toLowerCase() === target)
  return match?.User || null
}

// Walk every comment once and assemble per-matchId state. Bad/duplicate/
// out-of-turn protocol messages are simply ignored rather than throwing, so
// a malformed message can't wedge every match.
export const buildMatches = (comments) => {
  const matches = {}

  ;(comments || []).forEach((c) => {
    const payload = decode(c.comment)
    if (!payload?.matchId || !c.User) return

    const match = (matches[payload.matchId] ||= {
      matchId: payload.matchId,
      challenge: null,
      accept: null,
      commits: {},
      reveals: {}
    })

    if (payload.type === 'challenge' && !match.challenge) {
      match.challenge = c
      match.target = payload.target || null
      match.targetName = payload.targetName || null
      return
    }
    if (!match.challenge) return

    const challengerId = match.challenge.User.id
    if (
      payload.type === 'accept' &&
      !match.accept &&
      c.User.id !== challengerId &&
      (!match.target || c.User.id === match.target)
    ) {
      match.accept = c
      return
    }
    if (!match.accept) return

    const participantIds = [challengerId, match.accept.User.id]
    if (!participantIds.includes(c.User.id)) return

    if (payload.type === 'commit' && !match.commits[c.User.id] && payload.hash) {
      match.commits[c.User.id] = { hash: payload.hash }
      return
    }
    if (
      payload.type === 'reveal' &&
      match.commits[c.User.id] &&
      !match.reveals[c.User.id] &&
      MOVE_ALIASES[payload.move]
    ) {
      match.reveals[c.User.id] = { move: payload.move, nonce: payload.nonce }
    }
  })

  return matches
}

// Async because reveal verification hashes both moves — call once per
// rendered match, not per comment.
export const resolveMatch = async (match) => {
  const challengerId = match.challenge.User.id
  const opponentId = match.accept?.User.id
  if (!opponentId) return { phase: 'open' }

  const bothCommitted = match.commits[challengerId] && match.commits[opponentId]
  if (!bothCommitted) return { phase: 'choosing', challengerId, opponentId }

  const bothRevealed = match.reveals[challengerId] && match.reveals[opponentId]
  if (!bothRevealed) return { phase: 'revealing', challengerId, opponentId }

  const verify = async (id) => {
    const reveal = match.reveals[id]
    const hash = await hashMove(reveal.move, reveal.nonce)
    return hash === match.commits[id].hash ? reveal.move : null
  }

  const [challengerMove, opponentMove] = await Promise.all([verify(challengerId), verify(opponentId)])

  let outcome = 'tie'
  if (!challengerMove && !opponentMove) outcome = 'void'
  else if (!challengerMove) outcome = 'opponent'
  else if (!opponentMove) outcome = 'challenger'
  else if (challengerMove !== opponentMove) {
    outcome = BEATS[challengerMove] === opponentMove ? 'challenger' : 'opponent'
  }

  return { phase: 'complete', challengerId, opponentId, challengerMove, opponentMove, outcome }
}

export const moveIcon = (move) => MOVE_ICON[move] || '❔'

// Hook: owns the "moves I've locally committed to but not yet revealed"
// state, auto-reveals once both sides have committed, and exposes `run` for
// dispatching `/roshambo ...` sub-commands.
export const useRoshambo = ({ comments, currentUserId, onSend }) => {
  const pendingRef = useRef(loadPending())
  const matches = useMemo(() => buildMatches(comments), [comments])

  useEffect(() => {
    Object.entries(pendingRef.current).forEach(([matchId, pending]) => {
      const match = matches[matchId]
      if (!match?.accept) return

      const challengerId = match.challenge.User.id
      const opponentId = match.accept.User.id
      const bothCommitted = match.commits[challengerId] && match.commits[opponentId]
      const iAmParticipant = [challengerId, opponentId].includes(currentUserId)

      if (bothCommitted && iAmParticipant && !match.reveals[currentUserId]) {
        onSend(encode({ type: 'reveal', matchId, move: pending.move, nonce: pending.nonce }))
        delete pendingRef.current[matchId]
        savePending(pendingRef.current)
      }
    })
  }, [matches, currentUserId, onSend])

  const run = async (args) => {
    const [sub] = args

    if (!sub) {
      return { post: encode({ type: 'challenge', matchId: randomId() }) }
    }

    if (sub === 'join') {
      const candidates = Object.values(matches).filter(
        (m) =>
          !m.accept &&
          m.challenge.User.id !== currentUserId &&
          (!m.target || m.target === currentUserId)
      )
      const open = candidates.find((m) => m.target === currentUserId) || candidates.find((m) => !m.target)
      if (!open) return { local: 'No open Roshambo challenge to join. Start one with /roshambo' }
      return { post: encode({ type: 'accept', matchId: open.matchId }) }
    }

    const move = MOVE_ALIASES[sub]
    if (!move) {
      const target = findUserByUsername(comments, sub)
      if (!target) {
        return {
          local: `Couldn't find "${sub}" in this chat yet — they need to have sent a message first. Usage: /roshambo, /roshambo <username>, /roshambo join, or /roshambo rock|paper|scissors`
        }
      }
      if (target.id === currentUserId) return { local: "You can't challenge yourself." }

      return {
        post: encode({
          type: 'challenge',
          matchId: randomId(),
          target: target.id,
          targetName: getUserLabel(target)
        })
      }
    }

    const active = Object.values(matches).find(
      (m) =>
        m.accept &&
        [m.challenge.User.id, m.accept.User.id].includes(currentUserId) &&
        !m.commits[currentUserId]
    )
    if (!active) return { local: "You don't have an active Roshambo match waiting on your move." }

    const nonce = randomId()
    const hash = await hashMove(move, nonce)
    pendingRef.current[active.matchId] = { move, nonce }
    savePending(pendingRef.current)

    return { post: encode({ type: 'commit', matchId: active.matchId, hash }) }
  }

  return { matches, run }
}
